import './style.scss';
import React, { useEffect, useRef, useState, useMemo, Fragment } from 'react';
import { BuilderMobileNav, L2Parent, Leaf, transformToLeaf } from '../NAV_TYPES';
import { useQuery } from '@apollo/client';
import Skeleton from '@material-ui/lab/Skeleton';
import { loader } from 'graphql.macro';
import { ShippingCurrencyModalTrigger } from '../../../templates/_shared/ui-components/ShippingCurrencyModal/ShippingCurrencyModalTrigger';
import { Accordion, AccordionDetails, AccordionSummary, useMediaQuery } from '@material-ui/core';
import { MUI_DESKTOP_BREAKPOINT, MUI_MOBILE_BREAKPOINT } from '../../../utils/constants';
import { GWPandLoyaltyGeoSettings } from 'src/scripts/globals';
import AloSidebar from '../../../templates/_shared/ui-components/YogaUI/alo-ui/AloSidebar';
import { checkIfInVariation } from 'src/scripts/utils/optimizely';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { prependLocaleToPath } from '../../../utils/localization';
import { useCacheAndNetwork } from 'src/scripts/utils/react/useCacheAndNetwork';
import { fetchDataFromBuilder } from 'src/scripts/utils/builder';
import { SiteSettings } from 'src/scripts/utils/environment';
import { fireGA4MenuClickEvent } from 'src/scripts/utils/gtm-data-layer';

// OCN-4757
const shouldShowTextNavigation = checkIfInVariation({
  variationId: '29215420721',
  experimentId: '29249770495',
});

interface AccordionPanelProps {
  key: string;
  title: string;
  breadcrumbs: string;
  content: React.ReactNode;
}

const AccordionUI: React.FC<{ panel: AccordionPanelProps; index: number }> = ({ panel, index }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion
      key={panel.key}
      className={`nav-item__accordion ${expanded && `expanded`}`}
      expanded={expanded}
      onChange={() => {
        setExpanded((prevState) => !prevState);
        sendClickEventToHeap(panel.breadcrumbs);
      }}
    >
      <AccordionSummary
        classes={{ root: 'accordion-summary', content: 'accordion-summary-content' }}
        expandIcon={<ExpandMore />}
        aria-controls={`panel${index}-content`}
        id={`panel${index}-header`}
      >
        <p className="title">{panel.title}</p>
      </AccordionSummary>
      <AccordionDetails className="accordion-details">{panel.content}</AccordionDetails>
    </Accordion>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasChildren = (children: any[] | undefined | null) => {
  if (!children) return false;
  return children.length > 0;
};
const navDataQuery = loader('./../../header/GetNavData.shopify.graphql');

const shouldUseBuilderPoweredNav = SiteSettings?.builderPoweredMenus ?? false;

function MobileNav() {
  const mobileNavWrapperNode: React.RefObject<HTMLDivElement> = useRef(null);
  const [l1TabIndex, setL1TabIndex] = useState(0);

  const { data } = useQuery(navDataQuery, {
    variables: { handle: '___dummy-product-for-navigation-images' },
    fetchPolicy: 'cache-and-network',
    skip: shouldUseBuilderPoweredNav,
  });

  const shopify_NAV_DATA: Leaf[] | undefined = useMemo(() => {
    if (data) {
      try {
        return JSON.parse(data.productByHandle.metafield.value);
      } catch (error) {
        return null;
      }
    }
  }, [data]);

  const { data: builderResp } = useCacheAndNetwork<Leaf[]>('mobile-nav-data', getBuilderData, {
    staleCacheTTL: 1000 * 60 * 60 * 24,
    replaceStaleDataPostFetch: true,
    skipCache: !shouldUseBuilderPoweredNav,
    skipDataFetch: !shouldUseBuilderPoweredNav,
  });

  const NAV_DATA: Leaf[] = (shouldUseBuilderPoweredNav ? builderResp : shopify_NAV_DATA) ?? [];

  // OCN-4757
  useEffect(() => {
    const collapseMenu = document.getElementById('collapseMenu');
    if (collapseMenu && shouldShowTextNavigation) {
      collapseMenu.classList.add('test-variation');
    }
  }, []);

  const { isCustomer } = window.getCustomerInfo?.() || {};
  const isDesktop = useMediaQuery(MUI_DESKTOP_BREAKPOINT, { noSsr: true });
  if (isDesktop) return null; // haha this implies we do all this work & then choose to not render it

  return NAV_DATA && NAV_DATA.length > 0 ? (
    <div ref={mobileNavWrapperNode} id="MobileNavMenu">
      <div className="mobile-nav-header">
        <div
          onClick={() => {
            if (isCustomer) {
              window.location.href = prependLocaleToPath('/account');
            } else if (window.openSignInModal) {
              window.openSignInModal();
            }
          }}
          className="auth-action"
        >
          <span className="alo-account-icon"></span>
          {isCustomer ? (
            <h6 className="h6 uppercase">My Account</h6>
          ) : (
            <h6 className="h6 uppercase">Sign In to Get Rewards</h6>
          )}
        </div>
        <div
          className="close-button"
          onClick={() => {
            window.toggleMobileMenu && window.toggleMobileMenu();
          }}
        >
          <span className="alo-cross-primary-icon"></span>
        </div>
      </div>
      <div className="fake-search-input">
        <input
          type="text"
          className="main-nav__search-input main-nav__search-input--mobile"
          placeholder="Search"
          readOnly
          onClick={() => {
            window.toggleMobileSearch && window.toggleMobileSearch();
          }}
        />
        <div className="search-icon">
          <span className="alo-search-icon"></span>
        </div>
      </div>
      {
        <React.Fragment>
          <ul
            className={
              NAV_DATA.length <= 3
                ? 'l1-selection-tabs justify-content-center'
                : NAV_DATA.length === 4
                ? `l1-selection-tabs ${isDesktop ? `justify-content-center` : `justify-content-start`}`
                : 'l1-selection-tabs justify-content-start'
            }
          >
            {NAV_DATA.map((l1Data, idx) => {
              const isActive = idx === l1TabIndex;
              const { titleColor = 'var(--primary)', titleBadge, title, href, children } = l1Data;
              const handleTabAction = () => {
                if (href.length >= 1 && !hasChildren(children)) {
                  window.location.href = prependLocaleToPath(href);
                } else {
                  setL1TabIndex(idx);
                  document.getElementById('collapseMenu')?.scrollTo(0, 0);
                }
              };
              return (
                <li
                  role="tab"
                  tabIndex={0}
                  style={{
                    position: 'relative',
                    color: titleColor,
                    opacity: isActive ? 1 : '0.5',
                  }}
                  className="tab option"
                  key={idx}
                  onClick={handleTabAction}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleTabAction();
                    }
                  }}
                  aria-selected={isActive}
                  aria-controls={`tab-panel-${idx}`}
                >
                  <div className="menu-item-badge">{titleBadge}</div>
                  <span
                    className={`l1-tab-label ${isActive ? `active` : ''}`}
                    style={
                      {
                        color: titleColor,
                        // this is needed so that the :after underline on the nav link is able to inherit the color
                        '--nav-title-color': titleColor,
                      } as React.CSSProperties
                    }
                  >
                    {title}
                  </span>
                </li>
              );
            })}
          </ul>
          {shouldShowTextNavigation ? (
            <L2TextNavigationRender
              l1Index={l1TabIndex}
              //@ts-expect-error // ? [REVISIT]
              NAV_DATA={NAV_DATA}
            />
          ) : (
            <L2Render
              l1Index={l1TabIndex}
              //@ts-expect-error // ? [REVISIT]
              NAV_DATA={NAV_DATA}
            />
          )}
        </React.Fragment>
      }
    </div>
  ) : (
    <div id="MobileNavMenu" style={{ height: '100vh' }} />
  );
}

export default MobileNav;

const L2Render = ({ l1Index, NAV_DATA }: { l1Index: number; NAV_DATA: L2Parent[] }) => {
  const l2NodeRef: React.RefObject<HTMLDivElement> = useRef(null);
  const l1Data = NAV_DATA[l1Index];

  const [rewardsPageLink, setRewardsPageLink] = useState<'/pages/access' | '/pages/in-access'>('/pages/access');

  useEffect(() => {
    const conditionallySetAccessLink = async () => {
      const loyaltySettings = await GWPandLoyaltyGeoSettings();
      if (loyaltySettings.shouldRenderLoyaltyWidgetForINTL) {
        setRewardsPageLink('/pages/in-access');
      }
    };
    conditionallySetAccessLink();
  }, []);

  const [level3Visible, setL3visible] = useState(false);
  const [level3Data, setL3data] = useState<L3data | null>(null);

  const openL3Sidebar = (children: Leaf[], title: string) => {
    setL3visible(true);
    setL3data({ title, navItems: children });
  };
  interface L3data {
    title: string;
    navItems: Leaf[];
  }
  const validL1Children = l1Data?.children.filter((item) => item.title);

  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  return (
    <>
      <div
        className="l2-render"
        ref={l2NodeRef}
        role="tabpanel"
        id={`tab-panel-${l1Index}`}
        aria-labelledby={`tab-${l1Index}`}
        tabIndex={0}
      >
        {validL1Children?.map((item, idx) => {
          const { children, title, href, imageSrc, titleColor } = item;
          // ? [REVISIT] logic to add `nativeapp-hide`
          const followHideClassname =
            title === 'New Arrivals' || title === 'New In!' || title === 'New In' ? 'nativeapp-hide' : '';
          const breadcrumbs = `${l1Data?.title.trim().replace(/\s/g, '-')}-->${title.trim().replace(/\s/g, '-')}`;
          const rewardsL1 = href.includes('/pages/access');
          const customClass = title.toLowerCase().includes('new to alo') && 'custom-line-break';

          // abstract the common content but serve different elements if has/doesn't have children
          const l2Content = (
            <>
              {imageSrc ? (
                <img className="img-fluid" src={generateImgUrl(imageSrc) || ''} alt="" />
              ) : (
                <Skeleton variant="rect" height={isMobile ? 134 : 176} />
              )}
              <h3
                style={titleColor && titleColor.length > 3 ? { color: titleColor } : {}}
                className={`label body-small uppercase ${customClass}`}
              >
                {title}
              </h3>
            </>
          );

          return (
            <Fragment key={imageSrc}>
              {children && children.length > 0 ? (
                <div
                  className={`l2-item ${followHideClassname}`}
                  onClick={() => {
                    openL3Sidebar(children, title);
                    sendClickEventToHeap(breadcrumbs);
                  }}
                >
                  {l2Content}
                </div>
              ) : (
                <a
                  className={`l2-item ${followHideClassname}`}
                  href={rewardsL1 ? rewardsPageLink : href}
                  onClick={() => {
                    window.toggleMobileMenu && window.toggleMobileMenu();
                    sendClickEventToHeap(breadcrumbs);
                    fireGA4MenuClickEvent(title, l1Data?.title);
                  }}
                >
                  {l2Content}
                </a>
              )}
            </Fragment>
          );
        })}
      </div>
      <div style={{ marginBottom: 20 }}>
        <ShippingCurrencyModalTrigger />
      </div>
      <AloSidebar
        mobileWidth={`100vw`}
        onClose={(e) => {
          const shouldCloseSidebarOnly = (e?.target as Element)?.classList.contains('sidebar-title');
          setL3visible(false);
          if (!shouldCloseSidebarOnly && window.toggleMobileMenu) window.toggleMobileMenu();
        }}
        visible={level3Visible}
        title={level3Data?.title || 'menu'}
        headerBorder={true}
        anchor={isMobile ? 'right' : 'left'}
        desktopWidth={500}
      >
        <div className="l3-wrapper">
          {level3Data?.navItems.map((item) => (
            <a
              href={item.href}
              className="l3-item h6 grey"
              onClick={() => {
                const breadcrumbs = `${level3Data?.title.trim().replace(/\s/g, '-')}-->${item.title
                  .trim()
                  .replace(/\s/g, '-')}`;
                sendClickEventToHeap(breadcrumbs);
                fireGA4MenuClickEvent(item.title, l1Data?.title, level3Data?.title);
                window.toggleMobileMenu && window.toggleMobileMenu();
              }}
            >
              {item.title}
            </a>
          ))}
        </div>
      </AloSidebar>
    </>
  );
};

const L2TextNavigationRender = ({ l1Index, NAV_DATA }: { l1Index: number; NAV_DATA: L2Parent[] }) => {
  const l1Data = NAV_DATA[l1Index];
  const validL1Children = l1Data?.children.filter((item) => item.title);

  return (
    <>
      <div className="l2-container">
        {validL1Children?.map((L1Child) => {
          const { title, children, href } = L1Child;
          const breadcrumbsL1toL2 = `${l1Data?.title.trim().replace(/\s/g, '-')}-->${title.trim().replace(/\s/g, '-')}`;
          const validL2Children = children && children.filter((item) => item.title);

          const rootPanels = [
            {
              key: title,
              title: title,
              breadcrumbs: breadcrumbsL1toL2,
              content:
                validL2Children &&
                validL2Children.map((L2Child) => {
                  const { title, href } = L2Child;
                  const breadcrumbsL2toL3 = `${L1Child?.title
                    .trim()
                    .replace(/\s/g, '-')}-->${L2Child.title.trim().replace(/\s/g, '-')}`;
                  return (
                    <a
                      className="nav-link"
                      href={href}
                      onClick={() => {
                        sendClickEventToHeap(breadcrumbsL2toL3);
                        fireGA4MenuClickEvent(title);
                      }}
                    >
                      {title}
                    </a>
                  );
                }),
            },
          ];

          return (
            <>
              {children && children.length > 0 ? (
                rootPanels && rootPanels.map((panel, index) => <AccordionUI key={index} panel={panel} index={index} />)
              ) : (
                <div className="nav-item__non-accordion">
                  <a
                    className="title"
                    href={href}
                    onClick={() => {
                      const breadcrumbsL1toL3 = `${l1Data?.title.trim().replace(/\s/g, '-')}-->${title
                        .trim()
                        .replace(/\s/g, '-')}`;
                      sendClickEventToHeap(breadcrumbsL1toL3);
                    }}
                  >
                    {title}
                  </a>
                </div>
              )}
            </>
          );
        })}
      </div>
      <div className="shipping-currency-wrapper">
        <ShippingCurrencyModalTrigger />
      </div>
    </>
  );
};

// utils
// ! [REVISIT] move to shared import, in case our bundler is super dumb
const generateNavImgUrl = (imageSrc: string, size: string, crop = 'crop_center') => {
  if (!imageSrc) return undefined;
  const urlParts = imageSrc.replace(/\?.*/, '').split('/');
  //                                  Some examples to illustrate what each of these looks like
  const fileName = urlParts.pop(); // filename.jpg
  if (!fileName) return undefined;
  const urlBase = urlParts.join('/'); // https://.....products
  const parts = fileName.split('.');
  const baseName = parts.shift(); // "women--get-inspired-L2"
  const extension = 'progressive.' + parts.join('.'); // "progressive.png.jpg"
  // @ts-ignore
  const rightUri = `${urlBase}/${baseName}_${size}${crop ? `_${crop}` : ''}.${extension}?${window.Shopify.theme.id}`;
  return rightUri;
};

const generateImgUrl = (imageSrc: string) => {
  if (shouldUseBuilderPoweredNav) {
    return imageSrc;
  }
  return generateNavImgUrl(imageSrc, '330x255');
};
const sendClickEventToHeap = (breadcrumbs: string) => {
  try {
    heap.track('Mobile Nav Click', { breadcrumbs });
  } catch (error) {
    console.log(error);
  }
};

const getBuilderData = () =>
  fetchDataFromBuilder<BuilderMobileNav>('mobile-navigation-menu').then((resp) => {
    const mobileNavData: Leaf[] = resp.data.children.map(transformToLeaf);
    return mobileNavData;
  });

// Builder Data Fetching
